import { Observable } from 'rxjs';
import { PayloadApiResponse } from './types.domain';

export const DYNABOT_FEATURE_FLAG = 'dynabot';
export const DYNABOT_MAX_MESSAGES = 40;
export const DYNABOT_MAX_MESSAGE_LENGTH = 4000;

export type DynabotChatRole = 'user' | 'assistant';

export interface DynabotChatMessage {
  role: DynabotChatRole;
  content: string;
}

/**
 * The full visible conversation is resent each turn because the server keeps only the frozen
 * application context, never the conversation. Omitting sessionId starts a new session, which is
 * when the context snapshot is taken.
 */
export interface DynabotChatRequest {
  applicationId: number;
  sessionId?: string | null;
  messages: DynabotChatMessage[];
}

export type DynabotContextSectionStatus = 'loaded' | 'empty' | 'unavailable';

export interface DynabotContextSectionSummary {
  title: string;
  status: DynabotContextSectionStatus;
  /** Short, user-safe explanation for empty or unavailable sections. */
  detail: string | null;
  chars: number;
}

export interface DynabotChatResponse {
  sessionId: string;
  reply: string;
  /** Set when the session was created by this request, i.e. the context was just built. */
  contextBuiltAt: string | null;
  /** Set alongside contextBuiltAt, so the user can see what DynaBot was actually given. */
  contextSections: DynabotContextSectionSummary[] | null;
}

export type SendDynabotMessageFn = (request: DynabotChatRequest) => Observable<PayloadApiResponse<DynabotChatResponse | null>>;

/** Lowercase kebab-case `.md` names only, so a file name can never escape the knowledge folder. */
export const DYNABOT_KNOWLEDGE_FILE_NAME_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*\.md$/;
export const DYNABOT_KNOWLEDGE_FILE_NAME_MAX_LENGTH = 64;
/** Every knowledge file is sent in full with each new chat, so keep them bounded. */
export const DYNABOT_KNOWLEDGE_FILE_MAX_LENGTH = 50000;

export interface DynabotKnowledgeFile {
  fileName: string;
  /** The heading the file is given in the system prompt. */
  title: string;
  /** Raw file content, including HTML comments, which are stripped before reaching the model. */
  content: string;
  updatedAt: string;
}

export interface SaveDynabotKnowledgeFileRequest {
  fileName: string;
  content: string;
}

export type GetDynabotKnowledgeFilesFn = () => Observable<PayloadApiResponse<DynabotKnowledgeFile[]>>;
export type SaveDynabotKnowledgeFileFn = (request: SaveDynabotKnowledgeFileRequest) => Observable<PayloadApiResponse<DynabotKnowledgeFile>>;
